import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, Modal, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getClaimFormAlert,
  getClaimFormData,
  getClaimFormError,
  getClaimFormLoading,
  getVisibleFormData,
} from '../../selectors'
import { useDebounce } from 'src/react-app/hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'
import { isEqual } from 'lodash'
import produce from 'immer'

interface OrdersFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const Forms: React.FC<OrdersFormsProps> = memo((props: OrdersFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const claimFormData = useSelector(getClaimFormData)
  const claimFormIsLoading = useSelector(getClaimFormLoading)
  const claimFormError = useSelector(getClaimFormError)
  const claimFormAlert = useSelector(getClaimFormAlert)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const formRef = useRef<typeof visibleFormData>()
  const [prevFormData, setPrevFormData] = useState<any>(undefined)

  useEffect(() => {
    if (visibleFormData && claimFormData) {
      formRef.current = produce(visibleFormData, draft => {})
      setPrevFormData({ ...visibleFormData.resultObject })
    }
  }, [visibleFormData, claimFormData])

  const saveCard = useCallback(
    async (isNextStage = false) => {
      await saveResult(id, formRef.current, claimFormData?.stages?.current, isNextStage)
    },
    [id, formRef.current],
  )

  useEffect(() => {
    const redirectUrl = claimFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [claimFormData])

  const debounceSaveCase = useDebounce(saveCard, 1500)

  const onChangeForm = useCallback(
    async ({ formData }) => {
      if (!formRef.current) {
        return
      }
      interface IPriceData {
        price_delivery: null | number | string
        price_material: null | number | string
        price_work_close_complaints: null | number | string
        price_total: null | string
      }
      // Merge formData with current resultObject
      const updatedFormData = produce(formRef.current.resultObject, (draft: IPriceData) => {
        Object.assign(draft, formData)
        // Calculate price_total if payment fields are present
        if (
          'price_delivery' in formData ||
          'price_material' in formData ||
          'price_work_close_complaints' in formData
        ) {
          const priceDelivery = Number(draft?.price_delivery) || 0
          const priceMaterial = Number(draft?.price_material) || 0
          const priceWorkCloseComplaints = Number(draft?.price_work_close_complaints) || 0
          draft.price_total = (priceDelivery + priceMaterial + priceWorkCloseComplaints).toFixed(2)
        }
      })

      formRef.current = produce(formRef.current, draft => {
        draft.resultObject = updatedFormData
      })

      if (isEqual(prevFormData, updatedFormData)) {
        return
      }

      if (!prevFormData && !isEqual(prevFormData, updatedFormData)) {
        setPrevFormData(updatedFormData)
        return
      }

      try {
        debounceSaveCase()
      } finally {
        setPrevFormData(updatedFormData)
      }
    },
    [prevFormData, debounceSaveCase],
  )

  useEffect(() => {
    return () => {
      setPrevFormData(undefined)
    }
  }, [])

  useEffect(() => {
    if (claimFormAlert) {
      Modal.warning({
        title: 'Предупреждение',
        content: claimFormAlert,
      })
    }
  }, [claimFormAlert])

  if (claimFormError && !claimFormIsLoading && !claimFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
        карточку повторно.
      </HStack>
    )
  }

  return (
    <Form
      {...formItemLayout}
      layout="horizontal"
      form={form}
      scrollToFirstError={true}
      style={{ width: '100%' }}
    >
      {claimFormError && !claimFormIsLoading ? (
        <div className={'alertWrapper'}>
          <Alert
            message="При выполнении операции возникла ошибка:"
            showIcon
            type="error"
            description={claimFormError}
          />
        </div>
      ) : null}
      {!claimFormIsLoading ? (
        <VStack gap={'16'} max align={'stretch'} className={'claimForm'}>
          {formRef.current && (
            <DefaultJsonForm
              key={claimFormData?.stages?.current}
              formData={formRef.current.resultObject}
              schema={formRef.current.resultSchema}
              onChange={onChangeForm}
              orientation="horizontal"
            />
          )}
        </VStack>
      ) : (
        <Skeleton active />
      )}
    </Form>
  )
})

Forms.displayName = 'ClaimForm'
